import { useContext } from 'react';
import { Avatar, Stack, Tooltip, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

import { UserContext } from '@/services/auth';

/**
 * User pill rendered on the right side of the top bar. Reads the name/email the
 * guard already resolved via `GET /users/me` (the JWT carries neither) from
 * `UserContext` and shows the real name + initials. The tooltip carries
 * name · email · role.
 *
 * When there is no resolved name (e.g. the `superadmin` path skips `/users/me`),
 * falls back to a generic person icon + the role label (never the meaningless
 * JWT ObjectId digits). Returns `null` when there is no session.
 *
 * Purely informational — it does not navigate (returning to the search screen
 * is done from the brand logo and the header banner's "Cambiar empresa").
 */
export const UserPill = () => {
  const {
    isAdmin,
    isSuperAdmin,
    jwt,
    name: resolvedName,
    email,
  } = useContext(UserContext);

  if (!jwt) {
    return null;
  }

  const role = isSuperAdmin ? 'Superadmin' : isAdmin ? 'Admin' : 'Interno';
  const name = resolvedName.trim();
  const initials = name ? initialsFromName(name) : undefined;
  const primary = name || role;

  const tooltipLines = [name, [email, role].filter(Boolean).join(' · ')].filter(
    Boolean
  );

  return (
    <Tooltip
      title={
        <span style={{ whiteSpace: 'pre-line' }}>
          {tooltipLines.join('\n')}
        </span>
      }
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.light',
            color: 'primary.dark',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {initials ?? <PersonOutlineIcon fontSize="small" />}
        </Avatar>
        <Typography variant="body2" fontWeight={600}>
          {primary}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

/** "Santiago Cerrutti" → "SC"; single word → first two chars. */
function initialsFromName(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  return name.slice(0, 2).toUpperCase();
}
